import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMinus,
  FiPlus,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";

const colourPalette = {
  black: "#252525",
  blue: "#315e9f",
  brass: "#b68a3a",
  burgundy: "#7d3046",
  cobalt: "#244a9b",
  coral: "#e36d5a",
  cream: "#f1e6d4",
  gold: "#c5a04a",
  green: "#4f8f72",
  ivory: "#eee5d6",
  lavender: "#b9a7d7",
  lemon: "#efd75d",
  lilac: "#bda7d8",
  mint: "#9fcfbd",
  mixed: "linear-gradient(135deg, #ef9f70 0 33%, #a5cfc0 33% 66%, #315e9f 66%)",
  orange: "#e98d4c",
  pink: "#e6acae",
  plum: "#76516e",
  purple: "#a77bb5",
  red: "#cf4d45",
  seafoam: "#a8cfbd",
  teal: "#397b78",
  ultramarine: "#3153a4",
  vermilion: "#df563f",
  yellow: "#ecd25a",
};

const getColourValue = (label) => {
  const colourName = Object.keys(colourPalette).find((name) =>
    String(label).toLowerCase().includes(name)
  );
  return colourPalette[colourName] || "#d7c8b6";
};

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const {
    addToCart,
    isWishlisted,
    toggleWishlist,
  } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openAccordion, setOpenAccordion] = useState("details");
  const [added, setAdded] = useState(false);
  const [failedImages, setFailedImages] = useState([]);

  const images = useMemo(() => {
    if (!product) return [];
    const productImages = Array.isArray(product.images)
      ? product.images.filter(Boolean)
      : [];
    if (productImages.length) return [...new Set(productImages)];
    return product.image ? [product.image] : [];
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const otherProducts = productData.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );
    return [...sameCategory, ...otherProducts].slice(0, 4);
  }, [product]);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size?.[0] || "");
    setSelectedColor(product.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setOpenAccordion("details");
    setAdded(false);
    setFailedImages([]);
  }, [product]);

  useEffect(() => {
    if (!added) return undefined;
    const timer = window.setTimeout(() => setAdded(false), 1600);
    return () => window.clearTimeout(timer);
  }, [added]);

  if (!product) {
    return (
      <main className="product-detail-page" id="main-content">
        <section className="product-detail__not-found">
          <p className="product-detail__eyebrow">Collection</p>
          <h1>Product not found</h1>
          <p>This object may have moved or is no longer part of the collection.</p>
          <Link className="product-detail__primary-link" to="/shop">
            Back to shop
          </Link>
        </section>
      </main>
    );
  }

  const description = Array.isArray(product.description)
    ? product.description
    : [product.description].filter(Boolean);
  const sizes = Array.isArray(product.size) ? product.size.filter(Boolean) : [];
  const colours = Array.isArray(product.colors)
    ? product.colors.filter(Boolean)
    : [];
  const features = Array.isArray(product.Features)
    ? product.Features.filter(Boolean)
    : [];
  const stockLimit = Math.max(1, Number(product.stock) || 99);
  const wishlisted = isWishlisted(product.id);

  const markImageFailed = (source) => {
    setFailedImages((current) =>
      current.includes(source) ? current : [...current, source]
    );
  };

  const addProductToCart = (item, amount = 1, size, colour) => {
    addToCart(
      item.id,
      amount,
      size ?? item.size?.[0] ?? "",
      colour ?? item.colors?.[0] ?? ""
    );
    window.dispatchEvent(new CustomEvent("ottireluna:cart-open"));
  };

  const handleAddToCart = () => {
    addProductToCart(product, quantity, selectedSize, selectedColor);
    setAdded(true);
  };

  const accordionItems = [
    {
      id: "details",
      title: "Product details",
      content: (
        <div className="product-accordion__details">
          {features.length > 0 && (
            <ul>
              {features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          )}
          {product.attributes && (
            <dl>
              {Object.entries(product.attributes).map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ),
    },
    {
      id: "shipping",
      title: "Shipping & returns",
      content: (
        <div>
          <p>
            Orders are prepared within {siteConfig.shipping.dispatchDays}.
            Standard delivery is complimentary from {formatPrice(
              siteConfig.shipping.freeThreshold
            )}.
          </p>
          <p>
            Unused objects in their original packaging can be returned within
            30 days of delivery. Return shipping costs are the customer’s
            responsibility.
          </p>
          <div className="product-accordion__links">
            <Link to="/shipping">Shipping information</Link>
            <Link to="/returns">Returns policy</Link>
          </div>
        </div>
      ),
    },
    {
      id: "sustainability",
      title: "Sustainability",
      content: (
        <p>
          We choose considered materials and trusted production partners to
          create useful objects intended to be enjoyed for years. Packaging is
          kept compact and recyclable wherever possible.
        </p>
      ),
    },
  ];

  return (
    <main className="product-detail-page" id="main-content">
      <nav className="product-detail__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/shop">Shop</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <section className="product-detail__layout" aria-labelledby="product-title">
        <div
          className={`product-gallery ${images.length <= 1 ? "product-gallery--single" : ""}`}
        >
          <div className="product-gallery__stack">
            {images.length > 0 ? (
              images.map((image, index) => (
                <figure
                  className={`product-gallery__frame ${
                    selectedImage === index ? "is-selected" : ""
                  }`}
                  key={`${image}-${index}`}
                >
                  {failedImages.includes(image) ? (
                    <span className="product-gallery__error">Image unavailable</span>
                  ) : (
                    <img
                      src={image}
                      alt={`${product.name}${images.length > 1 ? `, view ${index + 1}` : ""}`}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      onError={() => markImageFailed(image)}
                    />
                  )}
                </figure>
              ))
            ) : (
              <div className="product-gallery__frame is-selected">
                <span className="product-gallery__error">Image unavailable</span>
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div className="product-gallery__controls" aria-label="Product images">
              {images.map((image, index) => (
                <button
                  type="button"
                  key={`${image}-control`}
                  className={selectedImage === index ? "is-active" : ""}
                  aria-label={`Show product image ${index + 1}`}
                  aria-pressed={selectedImage === index}
                  onClick={() => setSelectedImage(index)}
                >
                  <span />
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="product-detail__info">
          <p className="product-detail__eyebrow">{product.category}</p>
          <div className="product-detail__title-row">
            <h1 id="product-title">{product.name}</h1>
            <button
              type="button"
              className={`product-detail__wishlist ${wishlisted ? "is-active" : ""}`}
              aria-label={`${wishlisted ? "Remove" : "Add"} ${product.name} ${
                wishlisted ? "from" : "to"
              } wishlist`}
              aria-pressed={wishlisted}
              onClick={() => toggleWishlist(product.id)}
            >
              <FiHeart aria-hidden="true" />
            </button>
          </div>
          <p className="product-detail__price">{formatPrice(product.price)}</p>

          <div className="product-detail__description">
            {description.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <div className="product-detail__options">
            {colours.length > 0 && (
              <fieldset className="product-option">
                <legend>
                  Colour: <strong>{selectedColor}</strong>
                </legend>
                <div className="product-option__colours">
                  {colours.map((colour) => (
                    <button
                      type="button"
                      key={colour}
                      className={selectedColor === colour ? "is-active" : ""}
                      aria-label={`Select ${colour}`}
                      aria-pressed={selectedColor === colour}
                      onClick={() => setSelectedColor(colour)}
                    >
                      <span
                        aria-hidden="true"
                        style={{ "--swatch-colour": getColourValue(colour) }}
                      />
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            {sizes.length > 0 && (
              <fieldset className="product-option">
                <legend>
                  Size: <strong>{selectedSize}</strong>
                </legend>
                <div className="product-option__sizes">
                  {sizes.map((size) => (
                    <button
                      type="button"
                      key={size}
                      className={selectedSize === size ? "is-active" : ""}
                      aria-pressed={selectedSize === size}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
          </div>

          <div className="product-detail__purchase">
            <div className="product-quantity" aria-label="Quantity selector">
              <button
                type="button"
                aria-label="Decrease quantity"
                disabled={quantity === 1}
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              >
                <FiMinus aria-hidden="true" />
              </button>
              <output aria-live="polite" aria-label={`Quantity ${quantity}`}>
                {quantity}
              </output>
              <button
                type="button"
                aria-label="Increase quantity"
                disabled={quantity >= stockLimit}
                onClick={() =>
                  setQuantity((current) => Math.min(stockLimit, current + 1))
                }
              >
                <FiPlus aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="product-detail__add"
              disabled={Number(product.stock) === 0}
              onClick={handleAddToCart}
            >
              {Number(product.stock) === 0
                ? "Sold out"
                : added
                  ? "Added to cart"
                  : "Add to cart"}
            </button>
          </div>
          <p className="product-detail__dispatch">
            {Number(product.stock) === 0
              ? "Currently unavailable"
              : `In stock · Dispatches in ${siteConfig.shipping.dispatchDays}`}
          </p>

          <div className="product-accordion">
            {accordionItems.map((item) => {
              const expanded = openAccordion === item.id;
              return (
                <section className="product-accordion__item" key={item.id}>
                  <h2>
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={`product-accordion-${item.id}`}
                      onClick={() =>
                        setOpenAccordion((current) =>
                          current === item.id ? "" : item.id
                        )
                      }
                    >
                      <span>{item.title}</span>
                      <FiChevronDown aria-hidden="true" />
                    </button>
                  </h2>
                  <div
                    id={`product-accordion-${item.id}`}
                    className={`product-accordion__content ${expanded ? "is-open" : ""}`}
                    hidden={!expanded}
                  >
                    {item.content}
                  </div>
                </section>
              );
            })}
          </div>
        </aside>
      </section>

      <section className="product-related" aria-labelledby="related-title">
        <div className="product-related__header">
          <h2 id="related-title">You might also like</h2>
          <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
            View {product.category.toLowerCase()} <span aria-hidden="true">→</span>
          </Link>
        </div>
        <div className="product-related__grid">
          {relatedProducts.map((item) => {
            const image = item.images?.[0] || item.image;
            return (
              <article className="product-related__card" key={item.id}>
                <Link
                  className="product-related__media"
                  to={`/shopDetails/${item.id}`}
                  aria-label={`View ${item.name}`}
                >
                  {image && !failedImages.includes(image) ? (
                    <img
                      src={image}
                      alt={item.name}
                      loading="lazy"
                      decoding="async"
                      onError={() => markImageFailed(image)}
                    />
                  ) : (
                    <span>Image unavailable</span>
                  )}
                </Link>
                <div className="product-related__copy">
                  <Link to={`/shopDetails/${item.id}`}>
                    <h3>{item.name}</h3>
                  </Link>
                  <p>{formatPrice(item.price)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => addProductToCart(item)}
                >
                  Add to cart <FiPlus aria-hidden="true" />
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
